// Resolução do tenant (operador white-label) do request atual.
//
// Ordem de prioridade (client, apenas quando NEXT_PUBLIC_ENVIRONMENT=staging):
//   1. Param `?whitelabel=<slug>` (recebido em /encomendas) — sempre vence,
//      sobrescreve o cookie e é removido da URL assim que lido.
//   2. Header `Tenant: <slug>` injetado pelo gateway (ex.: "princesadoscampos").
//      Lido no SSR e semeado em window.__TENANT__ para o client.
//   3. Cookie `tenant` (persistido quando o ?whitelabel é visto).
//   4. Default: "embarca-ai".
//
// Fora do ambiente staging, o cookie/param de whitelabel é ignorado — o
// tenant vem só do header `Tenant` resolvido no servidor (SSR).
//
// O tenant define o operator token (X-API-TOKEN) usado no login/cadastro e o
// subdomínio consultado no Strapi (tema, logo, metadados).

export const TENANT_WINDOW_KEY = "__TENANT__";
export const TENANT_COOKIE = "tenant";
export const DEFAULT_TENANT = "embarca-ai";

const isStaging = process.env.NEXT_PUBLIC_ENVIRONMENT === "staging";

// O tenant é interpolado em `<script>` inline (layout raiz) e usado para montar
// consultas ao Strapi, então só um slug estrito passa. Header `Tenant` e cookie
// `tenant` são ambos controláveis pelo cliente — cookie não respeita origem, e
// o servidor lê o cookie sem o gate de staging.
const TENANT_SLUG_PATTERN = /^[a-z0-9_-]{1,64}$/;

function normalize(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const slug = value.trim().toLowerCase();
  if (!slug || !TENANT_SLUG_PATTERN.test(slug)) return null;
  return slug;
}

// ----------------------------- Server -----------------------------

// Tenant a partir do request (header `Tenant` → cookie `tenant`), ou null.
export function resolveTenantFromRequest(
  headerValue: string | null | undefined,
  cookieValue: string | null | undefined
): string | null {
  return normalize(headerValue) || normalize(cookieValue);
}

// Igual ao acima, mas cai para "embarca-ai" quando vazio.
export function resolveTenantOrDefault(
  headerValue: string | null | undefined,
  cookieValue: string | null | undefined
): string {
  return resolveTenantFromRequest(headerValue, cookieValue) || DEFAULT_TENANT;
}

// ----------------------------- Client -----------------------------

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function persistTenantCookie(slug: string): void {
  if (typeof document === "undefined") return;
  // 30 dias
  document.cookie = `${TENANT_COOKIE}=${encodeURIComponent(
    slug
  )}; path=/; max-age=${60 * 60 * 24 * 30}; samesite=lax`;
}

function deleteTenantCookie(): void {
  if (typeof document === "undefined") return;
  document.cookie = `${TENANT_COOKIE}=; path=/; max-age=0; samesite=lax`;
}

function whitelabelParam(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return normalize(new URLSearchParams(window.location.search).get("whitelabel"));
  } catch {
    return null;
  }
}

function stripWhitelabelParam(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.delete("whitelabel");
  window.history.replaceState(window.history.state, "", url.toString());
}

// Tenant atual no client: ?whitelabel (prioridade, sobrescreve o cookie) →
// window seed (header) → cookie → null. Cookie/param só valem em staging.
export function getTenant(): string | null {
  if (typeof window === "undefined") return null;

  if (isStaging) {
    // ?whitelabel sempre vence: sobrescreve o cookie existente, atualiza o
    // seed e remove o param da URL assim que confirmado.
    const param = whitelabelParam();
    if (param) {
      persistTenantCookie(param);
      (window as any)[TENANT_WINDOW_KEY] = param;
      stripWhitelabelParam();
      return param;
    }
  }

  const seeded = normalize((window as any)[TENANT_WINDOW_KEY]);
  if (seeded) return seeded;

  if (isStaging) {
    const cookie = normalize(readCookie(TENANT_COOKIE));
    if (cookie) {
      (window as any)[TENANT_WINDOW_KEY] = cookie;
      return cookie;
    }
  } else {
    // Fora do staging, nenhum cookie de whitelabel deve sobreviver.
    deleteTenantCookie();
  }

  return null;
}

// Tenant ou "embarca-ai" (default quando vazio).
export function getTenantOrDefault(): string {
  return getTenant() || DEFAULT_TENANT;
}

// Normaliza o valor cru do header `Tenant` (uso server pontual).
export function getTenantFromHeaders(
  headerValue: string | null | undefined
): string | null {
  return normalize(headerValue);
}
